<template>
  <div id="app">
    <div class="nav-container">
      <b-navbar v-if="signedIn" class="bg-primary justify-content-between px-2 py-0">
        <b-nav-text class="d-none d-sm-block font-sm text-white">
          Version: {{ version }}
        </b-nav-text>
        <b-nav-text class="d-none d-sm-block font-sm text-white">
          <span class="mx-2">Signed In As: {{fullName}}</span>
          <span class="mx-2">FOResT User ID: {{forestID}}</span>
        </b-nav-text>
        <b-nav-text class="d-block d-sm-none text-truncate font-sm text-white">
          <span class="mx-1">{{fullName}}</span>
        </b-nav-text>
        <b-nav-text class="d-block d-sm-none font-sm text-white">
          <span class="mx-1">ID: {{forestID}}</span>
        </b-nav-text>
      </b-navbar>
      <b-navbar
        toggleable="lg"
        class="shadow-sm p-0"
        type="light"
        variant="white"
      >
        <b-navbar-brand class="d-none d-lg-block" to="/">
          <img class="ml-4" alt="MFS logo" src="./assets/LogoMinusOutline.jpg">
        </b-navbar-brand>
        <b-navbar-brand class="d-block d-lg-none" :href="links.mfsHome" target="_blank">
          <img class="ml-4" alt="MFS logo" src="./assets/LogoMinusOutline.jpg">
        </b-navbar-brand>
        <b-navbar-toggle target="nav-collapse" class="mx-3" />
        <b-collapse id="nav-collapse" is-nav v-model="showCollapse" class="font-md">
          <b-navbar-nav class="text-center">
            <b-nav-text
              class="border-lg-left border-dark pl-4 font-lg text-secondary"
            >
              <a
                class="text-secondary d-none d-lg-block"
                :href="links.mfsHome"
                target="_blank"
                rel="noopener noreferrer"
              >
                Maine Forest Service
                <span v-if="$route.name !== 'home'" class="d-inline-block">
                  -
                  FOResT
                </span>
                <span v-else class="font-xs">
                  Version: {{ version }}
                </span>
              </a>
              <div v-if="$route.name !== 'home'" class="d-block d-lg-none">
                FOResT<span v-if="testEnvironment"> - TEST Env</span>
              </div>
            </b-nav-text>
          </b-navbar-nav>
          <b-navbar-nav v-if="testEnvironment"
            class="d-none d-lg-block ml-auto pl-3 pl-lg-0 pr-3">
            <b-nav-text class="font-lg text-secondary">
              TEST Environment
            </b-nav-text>
          </b-navbar-nav>
          <b-navbar-nav class="ml-auto pl-3 pl-lg-0 pr-3">
            <b-nav-item to="/" right class="mx-lg-2">
              <b-button variant="outline-primary" pill block class="d-lg-none col" aria-label="Home">
                Home
              </b-button>
              <a class="d-none d-lg-block">Home</a>
            </b-nav-item>
            <b-nav-item v-if="signedIn" to="/dashboard" right class="mx-lg-2">
              <b-button variant="outline-primary" pill block class="d-lg-none col" aria-label="Dashboard">
                Dashboard
              </b-button>
              <a class="d-none d-lg-block">Dashboard</a>
            </b-nav-item>
            <b-nav-item v-if="signedIn && !mfsUser && !tceoUser && !infwbUser" to="/myprofile" right class="mx-lg-2">
              <b-button variant="outline-primary" pill block class="d-lg-none col" aria-label="My Profile">
                My Profile
              </b-button>
              <a class="d-none d-lg-block">My Profile</a>
            </b-nav-item>
            <b-nav-item v-if="signedIn && mfsAdmin" to="/userManagement" right class="mx-lg-2">
              <b-button variant="outline-primary" pill block class="d-lg-none col" aria-label="User Management">
                User Management
              </b-button>
              <a class="d-none d-lg-block">User Management</a>
            </b-nav-item>
            <b-nav-item @click="openModal('contact-info')" right class="mx-lg-2">
              <b-button variant="outline-primary" pill block class="d-lg-none">
                Contact Us
              </b-button>
              <a class="d-none d-lg-block">Contact Us</a>
            </b-nav-item>
            <SignInModal class="mt-2 mt-lg-0 mx-lg-2" @modalOpened="closeCollapse" />
            <b-nav-item :class="signedIn ? 'd-block d-md-none' : 'd-block d-lg-none'">
              <b-nav-text class="font-sm float-right">
                Version: {{ version }}
              </b-nav-text>
            </b-nav-item>
          </b-navbar-nav>
        </b-collapse>
        <b-modal
          ref="contact-info"
          body-class="pb-5"
          content-class="px-3"
          hide-header
          hide-footer
          centered
          >
          <h3 class="text-center pb-3">Contact Us</h3>
          <p class="text-center">
            <a href="mailto:forestinfo@maine.gov">
              forestinfo@maine.gov
            </a>
          </p>
          <p class="text-center">
            <a class="text-center" href="tel:207-287-2791">207-287-2791</a>
          </p>
        </b-modal>
        <b-modal
          v-if="testEnvironment"
          ref="test-site-info"
          body-class="pb-5"
          content-class="px-3"
          hide-header
          hide-footer
          centered
        >
          <h3 class="text-center pb-3">TEST Environment</h3>
          <p>
            This is the test version of the Forest Online Resource Tool, or FOResT, of the Maine
            Forest Service. Any data entered or generated in here is for testing only and is not
            valid by the Maine Forest Service.
          </p>
        </b-modal>
      </b-navbar>
    </div>
    <router-view></router-view>
    <IsExtraSmall @isExtraSmall="setIsExtraSmall" />
    <TimeoutModal />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import SignInModal from '@/components/SignInModal.vue';
import TimeoutModal from '@/components/TimeoutModal.vue';
import IsExtraSmall from './components/IsExtraSmall.vue';

export default {
  name: 'App',
  components: {
    SignInModal,
    TimeoutModal,
    IsExtraSmall,
  },
  computed: {
    ...mapState('User', {
      forestID: state => state.user.forestID,
      signedIn: state => state.signedIn,
    }),
    ...mapState('Configuration', {
      links: state => state.links,
      configuration: state => state.configuration,
    }),
    ...mapGetters('Configuration', ['testEnvironment']),
    ...mapGetters('User', ['fullName', 'mfsUser', 'mfsAdmin', 'tceoUser', 'infwbUser']),
  },
  data() {
    return {
      version: '',
      showCollapse: false,
    };
  },
  created() {
    this.$store.dispatch('Configuration/getConfiguration')
      .then(() => {
        const rollbarConfig = {};
        if (this.configuration.rollbarAccessToken) {
          rollbarConfig.accessToken = this.configuration.rollbarAccessToken;
        }
        if (this.configuration.rollbarEnvironment) {
          rollbarConfig.payload = { environment: this.configuration.rollbarEnvironment };
        }
        if (Object.keys(rollbarConfig).length) {
          this.$rollbar.configure(rollbarConfig);
        }
        if (this.testEnvironment) this.openModal('test-site-info');
      });
  },
  methods: {
    getVersion() {
      return this.$http
        .get('version')
        .then((response) => {
          this.version = response.data;
        });
    },
    setIsExtraSmall(isXS) {
      this.$store.commit('setIsExtraSmall', isXS);
    },
    openModal(type) {
      this.$refs[type].show();
      this.closeCollapse();
    },
    closeCollapse() {
      this.showCollapse = false;
    },
  },
  mounted() {
    this.getVersion();
    this.$store.dispatch('User/getUserInfo');
  },
};
</script>
